import { Block, Flex } from '@aloft/toolkit-sui';

import { memo, useMemo, useRef } from 'react';

import { useComparablesToastAnchor } from '../../hooks/useToaster/useComparablesToastAnchor.ts';

import { LoadingScreenSamFull } from '../../components/LoadingScreen/LoadingScreenSamFull.tsx';
import type { LongLoaderProps } from '../../components/LoadingScreen/types.ts';
import { PropertyGrid } from '../../components/PropertyGrid/PropertyGrid.tsx';
import { getTabBackgroundColor } from '../../components/PropertyGrid/utils.ts';
import { useRentalComparablesInitialRecommendations } from '../../hooks/useRentalComparablesInitialRecommendations.ts';
import { selectAivArvActiveTab } from '../../redux/features/selectors/uiSelectors.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { useTkv2ReferenceBarEvents } from '../../components/ReferenceBar/useTkv2ReferenceBarEvents.ts';
import { FeatureGridDataSourcesSection } from './columnSections/FeatureGridDataSourcesSection.tsx';
import { FeatureGridFeatureSections } from './columnSections/FeatureGridFeatureSections.tsx';
import { FeatureGridMainDwellingSection } from './columnSections/FeatureGridMainDwellingSection.tsx';
import { RentalFeatureGridTransactionSection } from './columnSections/RentalFeatureGridTransactionSection.tsx';
import { FeatureGridSiteSection } from './columnSections/FeatureGridSiteSection.tsx';
import { FeatureGridLabelColumnHeader } from './components/FeatureGridLabelColumnHeader.tsx';
import { FeatureGridToolbar } from './components/FeatureGridToolbar.tsx';

const rentalComparablesInitialLoadLongLoaderProps: LongLoaderProps = {
  estimatedDurationSeconds: 10,
  messageBreakpoints: [
    {
      breakpoint: 0.06,
      message: 'Searching the market for rental listings near your subject',
    },
    { breakpoint: 0.28, message: 'Ranking listings by similarity to your property' },
    { breakpoint: 0.52, message: 'Selecting the best rental comparables for your grid' },
    { breakpoint: 0.76, message: 'Saving rental comparables to your order' },
  ],
};

export const RentalFeatureGridModule = memo(() => {
  const containerRef = useRef<HTMLDivElement>(null);
  useComparablesToastAnchor(containerRef, 'rental-comparables');
  const { showRentalComparablesInitialLoad } = useRentalComparablesInitialRecommendations();
  const activeTab = useAppSelector(selectAivArvActiveTab);

  const columnSections = useMemo(
    () => [
      FeatureGridDataSourcesSection,
      RentalFeatureGridTransactionSection,
      FeatureGridSiteSection,
      FeatureGridMainDwellingSection,
      FeatureGridFeatureSections,
    ],
    [],
  );

  return (
    <>
      <LoadingScreenSamFull
        isLoading={showRentalComparablesInitialLoad}
        wrapperProps={{ backgroundColor: 'surface1' }}
        longLoaderProps={rentalComparablesInitialLoadLongLoaderProps}
      />
      <Flex
        ref={containerRef}
        {...useTkv2ReferenceBarEvents()}
        flexDirection="column"
        overflow="hidden"
        width="100%"
        height="100%"
        gap="8px"
        paddingTop="10px"
        backgroundColor={getTabBackgroundColor(activeTab)}
      >
        <FeatureGridToolbar />
        <Block overflow="auto" paddingLeft="10px" paddingRight="30px">
          <PropertyGrid
            gridType="FeatureGrid"
            columnSections={columnSections}
            listingType="rentalListing"
            labelColumnHeader={<FeatureGridLabelColumnHeader />}
          />
        </Block>
      </Flex>
    </>
  );
});
